import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import {
  FiltrosVagas,
  VagaProgramaResponseDto,
  VagaProgramaRequest,
  NivelVaga,
  OrigemVaga, JustificativaRequestDto,
} from '../model/vaga.model';
import { environment } from '../../../environments/environment';
import {PageModel} from '../models/pagination/page.model';
import {DateUtil} from '../utils/date.util';

@Injectable({
  providedIn: 'root',
})
export class VagaProgramaService {
  private apiUrl = `${environment.fullApiUrl}`;
  private vagasUrl = `${this.apiUrl}/vaga-programa`;
  private http = inject(HttpClient);

  obterNiveisVaga(): Observable<NivelVaga[]> {
    return this.http.get<NivelVaga[]>(`${this.apiUrl}/nivel-vaga`);
  }

  obterOrigensVaga(): Observable<OrigemVaga[]> {
    return this.http.get<OrigemVaga[]>(`${this.apiUrl}/origem-vaga`);
  }

  buscaPaginada(
    filtros: FiltrosVagas = {},
    page: number = 0,
    size: number = 10
  ): Observable<PageModel<VagaProgramaResponseDto>> {
    const filterParts: string[] = [];
    const codigosVagaFiltro = Array.isArray(filtros.codigosVaga)
      ? filtros.codigosVaga
          .map((codigo) => (codigo ?? '').trim())
          .filter((codigo) => !!codigo)
          .join(',')
      : filtros.codigosVaga;

    if (filtros.dtCriacao) {
      filtros.dtCriacao = DateUtil.toOtherFormat(filtros.dtCriacao, 'dd/MM/yyyy', 'yyyy-MM-dd');
    }

    if (filtros.dtAcao) {
      filtros.dtAcao = DateUtil.toOtherFormat(filtros.dtAcao, 'dd/MM/yyyy', 'yyyy-MM-dd');
    }

    const addFilter = (field: any, key: string) => {
      if (field !== null && field !== undefined && field !== '') {
        filterParts.push(`${key}==${field}`);
      }
    };

    const filterMap: Record<string, any> = {
      idVaga: filtros?.idVaga,
      // PGPP-557: situação por id (tempo real); descrição mantida por compatibilidade
      situacoes: filtros?.situacaoId,
      situacao: filtros?.situacaoId ? undefined : filtros?.situacao,
      programaId: filtros?.programaId ?? filtros?.programa,
      nivelVagaId: filtros?.nivelVaga,
      regiaoId: filtros?.regiao,
      ufId: filtros?.uf,
      municipioId: filtros?.municipio,
      dseiId: filtros?.dsei,
      tipoEquipeId: filtros?.tipoEquipeId,
      origemVagaId: filtros?.origemVagaId,
      codigosVaga: codigosVagaFiltro,
      codCir: filtros?.codCir,
      codCnes: filtros?.codCnes,
      codIvs: filtros?.codIvs,
      modalidadeFinanciamentoId: filtros?.modalidadeFinanciamentoId,
      codIne: filtros?.codIne,
      codIbgeNivel: filtros?.codIbgeNivel,
      codIbgeVaga: filtros?.codIbgeVaga,
      dtCriacao: filtros?.dtCriacao,
      dtAcao: filtros?.dtAcao,
      cicloId: filtros?.cicloId
    };

    Object.entries(filterMap).forEach(([key, value]) => addFilter(value, key));

    const params = new HttpParams()
      .set('filter', filterParts.join(';'))
      .set('page', page.toString())
      .set('size', size.toString())
      .set('sort', 'id,desc');

    return this.http
      .get<PageModel<VagaProgramaResponseDto>>(`${this.vagasUrl}/search`, { params })
      .pipe(
        catchError((error) => {
          console.error('Erro ao buscar vagas paginadas:', error);
          return of({
            content: [],
            page: { totalElements: 0, totalPages: 0, size, number: 0 }
          });
        })
      );
  }

  obterPorId(id: number): Observable<VagaProgramaResponseDto> {
    return this.http.get<VagaProgramaResponseDto>(`${this.vagasUrl}/${id}`);
  }

  salvar(vaga: VagaProgramaRequest): Observable<VagaProgramaResponseDto> {
    return this.http.post<VagaProgramaResponseDto>(`${this.vagasUrl}/salvar`, vaga);
  }

  temCargaInicial(cicloId: number): Observable<boolean> {
    return this.http.get<boolean>(`${this.vagasUrl}/ciclo/${cicloId}/tem-vagas`);
  }

  atualizar(id: number, vaga: VagaProgramaRequest): Observable<VagaProgramaResponseDto> {
    return this.http.put<VagaProgramaResponseDto>(`${this.vagasUrl}/${id}`, vaga);
  }

  inativar(id: number, dto: JustificativaRequestDto): Observable<VagaProgramaResponseDto> {
    return this.http.patch<VagaProgramaResponseDto>(`${this.vagasUrl}/${id}`, dto);
  }

  excluir(id: number, dto: JustificativaRequestDto): Observable<void> {
    return this.http.patch<void>(`${this.vagasUrl}/${id}/excluir`, dto);
  }
}
